/**
 * apiEndpoints.js
 *
 * Single source of truth for all API base URLs and endpoint paths.
 * Services should import from here instead of scattering process.env calls.
 *
 * Environment variables:
 *   REACT_APP_API_BASE_URL_USER  — User/Auth service base URL
 *   REACT_APP_API_BASE_URL_PMS   — Property Management System base URL
 */

// ─── Base URLs ────────────────────────────────────────────────────────────────
export const BASE_URL_USER = process.env.REACT_APP_API_BASE_URL_USER;
export const BASE_URL_PMS  = process.env.REACT_APP_API_BASE_URL_PMS;

// ─── API Version ──────────────────────────────────────────────────────────────
const API_VERSION = '/api/v1';

// ─── Per-service base paths ───────────────────────────────────────────────────
const USER_API = `${BASE_URL_USER}${API_VERSION}`;
const PMS_API  = `${BASE_URL_PMS}${API_VERSION}`;

// ─── Auth Endpoints ───────────────────────────────────────────────────────────
export const AUTH = {
    LOGIN:           `${USER_API}/auth/login`,
    LOGOUT:          `${USER_API}/auth/logout`,
    REFRESH:         `${USER_API}/auth/refresh`,
    FORGOT_PASSWORD: `${USER_API}/auth/forgot-password`,
    VERIFY_OTP:      `${USER_API}/auth/verify-otp`,
    RESET_PASSWORD:  `${USER_API}/auth/reset-password`,
    ME:              `${USER_API}/auth/me`,
    CHANGE_PASSWORD: `${USER_API}/auth/me/password`,
    PERMISSIONS:     `${USER_API}/auth/permissions`,
    MFA_SETUP:       `${USER_API}/auth/me/mfa/setup`,
    MFA_VERIFY:      `${USER_API}/auth/mfa/verify`,
    MFA_TOGGLE:      `${USER_API}/auth/me/mfa`,
    // Session Management
    SESSION_EXTEND:  `${USER_API}/auth/session/extend`,
    SESSIONS:        `${USER_API}/auth/sessions`,
    SESSION_BY_ID:   (id) => `${USER_API}/auth/sessions/${id}`,
    // Audit Logs
    AUDIT_LOGS:      `${USER_API}/auth/audit-logs`,
    AUDIT_LOGS_EXPORT: `${USER_API}/auth/audit-logs/export`,
    // Tenant Management
    TENANTS:         `${USER_API}/auth/tenants`,
    TENANT_SWITCH:   `${USER_API}/auth/tenants/switch`,
};

// ─── Notification Endpoints ───────────────────────────────────────────────────
export const NOTIFICATIONS = {
    BASE: `${PMS_API}/notifications`,
    READ_ALL: `${PMS_API}/notifications/read-all`,
    UNREAD_COUNT: `${PMS_API}/notifications/unread-count`,
    PUSH_REGISTER: `${PMS_API}/notifications/push/register`,
    PUSH_UNREGISTER: `${PMS_API}/notifications/push/unregister`,
    HISTORY: `${PMS_API}/notifications/history`,
    RETRY_HISTORY: (id) => `${PMS_API}/notifications/history/${id}/retry`,
    TEST_EVENT: `${PMS_API}/notifications/test`,
};

// ─── User Endpoints ───────────────────────────────────────────────────────────
export const USERS = {
    BASE:  `${USER_API}/users`,
    BY_ID: (id) => `${USER_API}/users/${id}`,
    EXPORT:          `${USER_API}/users/export`,
    ACTIVITY:        (id) => `${USER_API}/users/${id}/activity`,
    USER_PERMISSIONS:(id) => `${USER_API}/users/${id}/permissions`,
    USER_ROLE:       (id) => `${USER_API}/users/${id}/role`,
    STATUS:          (id) => `${USER_API}/users/${id}/status`,
    SECURITY:        (id) => `${USER_API}/users/${id}/security`,
    RESET_PASSWORD:  (id) => `${USER_API}/users/${id}/reset-password`,
    IMPORT_VALIDATE: `${USER_API}/users/import/validate`,
    IMPORT_EXECUTE:  `${USER_API}/users/import/execute`,
    UNLOCK:          (id) => `${USER_API}/users/${id}/unlock`,
};

// ─── Role Endpoints ───────────────────────────────────────────────────────────
export const ROLES = {
    BASE:  `${USER_API}/roles`,
    BY_ID: (id) => `${USER_API}/roles/${id}`,
    PERMISSIONS_MATRIX: `${USER_API}/roles/permissions`,
};

// ─── Hotel Endpoints ──────────────────────────────────────────────────────────
export const HOTELS = {
    BASE:  `${PMS_API}/hotels`,
    BY_ID: (id) => `${PMS_API}/hotels/${id}`,
    STATUS: (id) => `${PMS_API}/hotels/${id}/status`,
    ARCHIVE: (id) => `${PMS_API}/hotels/${id}/archive`,
    RESTORE: (id) => `${PMS_API}/hotels/${id}/restore`,
    MANAGE_ADMIN: (id) => `${PMS_API}/hotels/${id}/admin`,
    EXPORT: `${PMS_API}/hotels/export`,
    AUDIT_LOGS: (id) => `${PMS_API}/hotels/${id}/audit-logs`,
    CLONE: `${PMS_API}/hotels/clone`,
};

// ─── Room Endpoints ───────────────────────────────────────────────────────────
export const ROOMS = {
    BASE:  `${PMS_API}/rooms`,
    BY_ID: (id) => `${PMS_API}/rooms/${id}`,
    ARCHIVE: (id) => `${PMS_API}/rooms/${id}/archive`,
    OUT_OF_ORDER: (id) => `${PMS_API}/rooms/${id}/out-of-order`,
    RESOLVE_OOO: (id, oooId) => `${PMS_API}/rooms/${id}/out-of-order/${oooId}/resolve`,
    EXTEND_OOO: (id, oooId) => `${PMS_API}/rooms/${id}/out-of-order/${oooId}/extend`,
    BULK_STATUS: `${PMS_API}/rooms/bulk-status`,
};

// ─── Settings Endpoints ──────────────────────────────────────────────────────
export const SETTINGS = {
    BRAND:           `${PMS_API}/settings/brand`,
    BRANDING:        `${PMS_API}/settings/branding`,
    PUBLIC_BRANDING: `${PMS_API}/public/branding`,
    POLICIES:        `${PMS_API}/settings/policies`,
    CANCELLATION_POLICIES: `${PMS_API}/settings/cancellation-policies`,
    CANCELLATION_RULES: (id) => `${PMS_API}/settings/cancellation-rules/${id}`,
    CANCELLATION_REASONS: `${PMS_API}/settings/cancellation-reasons`,
    GLOBAL_DEFAULT_CANCELLATION_POLICY: `${PMS_API}/settings/cancellation-policies/global-default`,
    LOCALIZATION: `${PMS_API}/settings/localization`,
    TAXES: {
        BASE:  `${PMS_API}/settings/tax-slabs`,
        BY_ID: (id) => `${PMS_API}/settings/tax-slabs/${id}`,
    },
    BOOKING_SOURCES: {
        BASE:  `${PMS_API}/settings/booking-sources`,
        BY_ID: (id) => `${PMS_API}/settings/booking-sources/${id}`,
    },
    PAYMENT_METHODS: {
        BASE:  `${PMS_API}/settings/payment-methods`,
        BY_ID: (id) => `${PMS_API}/settings/payment-methods/${id}`,
    },
    EMAIL_TEMPLATES: {
        BASE:  `${PMS_API}/settings/notifications/templates`,
        BY_ID: (id) => `${PMS_API}/settings/notifications/templates/${id}`,
        TEST:  (id) => `${PMS_API}/settings/notifications/templates/${id}/test`,
    },
    NOTIFICATIONS: {
        TEMPLATES: `${PMS_API}/settings/notifications/templates`,
        TEMPLATE_BY_ID: (id) => `${PMS_API}/settings/notifications/templates/${id}`,
        TEST_TEMPLATE: (id) => `${PMS_API}/settings/notifications/templates/${id}/test`,
        TRIGGERS: `${PMS_API}/settings/notifications/triggers`,
        TRIGGER_BY_ID: (id) => `${PMS_API}/settings/notifications/triggers/${id}`,
        CHANNELS: `${PMS_API}/settings/notifications/channels`,
        CHANNEL_BY_TYPE: (type) => `${PMS_API}/settings/notifications/channels/${type}`,
    },
};

// ─── Setup Endpoints ──────────────────────────────────────────────────────────
export const SETUP = {
    ROOM_TYPES: `${PMS_API}/setup/room-types`,
    FLOORS: `${PMS_API}/setup/floors`,
    WINGS: `${PMS_API}/setup/wings`,
    AMENITIES: `${PMS_API}/setup/amenities`,
};
// ─── Rate Endpoints ───────────────────────────────────────────────────────────
export const RATES = {
    PLANS: `${PMS_API}/rates/plans`,
    BY_ID: (id) => `${PMS_API}/rates/plans/${id}`,
    CALENDAR: `${PMS_API}/rates/calendar`,
    STAY_FEES: {
        BASE: `${PMS_API}/rates/stay-fees`,
        BY_ID: (id) => `${PMS_API}/rates/stay-fees/${id}`,
    },
    EXPORT: `${PMS_API}/rates/export`,
};

// ─── Staff Endpoints ──────────────────────────────────────────────────────────
export const STAFF = {
    BASE:  `${PMS_API}/staff`,
    BY_ID: (id) => `${PMS_API}/staff/${id}`,
    DEACTIVATE: (id) => `${PMS_API}/staff/${id}/deactivate`,
    SHIFTS: `${PMS_API}/staff/shifts`,
    SCHEDULE: `${PMS_API}/staff/schedule`,
    ASSIGN_SHIFT: `${PMS_API}/staff/schedule/assign`,
    ATTENDANCE: `${PMS_API}/staff/attendance`,
    LEAVES: `${PMS_API}/staff/leave-requests`,
    LEAVE_BY_ID: (id) => `${PMS_API}/staff/leave-requests/${id}`,
    LEAVE_APPROVE: (id) => `${PMS_API}/staff/leave-requests/${id}/approve`,
    LEAVE_CANCEL: (id) => `${PMS_API}/staff/leave-requests/${id}/cancel`,
    VOID_NOTE: (noteId) => `${PMS_API}/staff/notes/${noteId}/void`,
};

// ─── Departments Endpoints ────────────────────────────────────────────────────
export const DEPARTMENTS = `${PMS_API}/departments`;

// ─── Guests Endpoints ───────────────────────────────────────────────────────────
export const GUESTS = {
    BASE: `${PMS_API}/guests`,
    BY_ID: (id) => `${PMS_API}/guests/${id}`,
    STAY_HISTORY: (id) => `${PMS_API}/guests/${id}/stay-history`,
    MERGE: `${PMS_API}/guests/merge`,
};

// ─── Reservations Endpoints ───────────────────────────────────────────────────
export const RESERVATIONS = {
    BASE: `${PMS_API}/reservations`,
    BY_ID: (id) => `${PMS_API}/reservations/${id}`,
    GROUP: `${PMS_API}/reservations/group`,
    CALENDAR: `${PMS_API}/reservations/calendar`,
    MOVE_ROOM: (id) => `${PMS_API}/reservations/${id}/move-room`,
    CANCEL: (id) => `${PMS_API}/reservations/${id}/cancel`,
    NO_SHOW: (id) => `${PMS_API}/reservations/${id}/no-show`,
    REFUND: (id) => `${PMS_API}/reservations/${id}/refund`,
    EXPORT: `${PMS_API}/reservations/export`,
    RATE_OVERRIDES: `${PMS_API}/reservations/rate-overrides`,
    APPROVE_RATE_OVERRIDE: (id) => `${PMS_API}/reservations/rate-overrides/${id}/approve`,
    REJECT_RATE_OVERRIDE: (id) => `${PMS_API}/reservations/rate-overrides/${id}/reject`,
    APPROVE_GOVERNMENT: (id) => `${PMS_API}/reservations/${id}/approve-government`,
};

// ─── Housekeeping Endpoints ───────────────────────────────────────────────────
export const HOUSEKEEPING = {
    TASKS: `${PMS_API}/housekeeping/tasks`,
    TASK_BY_ID: (id) => `${PMS_API}/housekeeping/tasks/${id}`,
    DASHBOARD: `${PMS_API}/housekeeping/dashboard`,
    TASK_TYPES: `${PMS_API}/housekeeping/task-types`,
    LOST_FOUND: `${PMS_API}/housekeeping/lost-found`,
    CLAIM_ITEM: (id) => `${PMS_API}/housekeeping/lost-found/${id}/claim`,
    DISCARD_ITEM: (id) => `${PMS_API}/housekeeping/lost-found/${id}/discard`,
    PERFORMANCE_REPORT: `${PMS_API}/housekeeping/reports/housekeeper-performance`,
};

// ─── Media Endpoints ──────────────────────────────────────────────────────────
export const MEDIA = {
    UPLOAD: `${PMS_API}/media/upload`,
};

// ─── OTA Endpoints ────────────────────────────────────────────────────────────
export const OTA = {
    CHANNELS: `${PMS_API}/ota/channels`,
    CHANNEL_BY_ID: (id) => `${PMS_API}/ota/channels/${id}`,
    UPDATE_ROOM_MAPPING: (id) => `${PMS_API}/ota/channels/${id}/room-mapping`,
    UPDATE_RATE_MAPPING: (id) => `${PMS_API}/ota/channels/${id}/rate-mapping`,
    SYNC_CHANNEL: (id) => `${PMS_API}/ota/channels/${id}/sync`,
    STOP_SELL_CHANNEL: (id) => `${PMS_API}/ota/channels/${id}/stop-sell`,
    SET_RESTRICTIONS_CHANNEL: (id) => `${PMS_API}/ota/channels/${id}/restrictions`,
    BOOKINGS_LOG: `${PMS_API}/ota/bookings/logs`,
    RETRY_BOOKING_LOG: (id) => `${PMS_API}/ota/bookings/logs/${id}/retry`,
    CHANNEL_PERFORMANCE: `${PMS_API}/ota/reports/performance`,
};

// ─── Procurement / Inventory Endpoints ─────────────────────────────────────────
export const SUPPLIERS = {
    BASE: `${PMS_API}/suppliers`,
    BY_ID: (id) => `${PMS_API}/suppliers/${id}`,
};

export const INVENTORY = {
    ITEMS: `${PMS_API}/inventory/items`,
    ITEM_BY_ID: (id) => `${PMS_API}/inventory/items/${id}`,
    ITEM_ADJUST: (id) => `${PMS_API}/inventory/items/${id}/adjust`,
    ITEM_MOVEMENTS: (id) => `${PMS_API}/inventory/items/${id}/movements`,
    PURCHASE_ORDERS: `${PMS_API}/inventory/po`,
    PO_BY_ID: (id) => `${PMS_API}/inventory/po/${id}`,
    PO_SUBMIT: (id) => `${PMS_API}/inventory/po/${id}/submit`,
    PO_APPROVE: (id) => `${PMS_API}/inventory/po/${id}/approve`,
    PO_RECEIVE: (id) => `${PMS_API}/inventory/po/${id}/receive`,
    PO_PAYMENT: (id) => `${PMS_API}/inventory/po/${id}/payment`,
};
// ─── Restaurant / POS Endpoints ───────────────────────────────────────────────
export const RESTAURANT = {
    CATEGORIES: `${PMS_API}/restaurant/categories`,
    ITEMS: `${PMS_API}/restaurant/items`,
    ORDERS: `${PMS_API}/restaurant/orders`,
    OPERATING_HOURS: `${PMS_API}/restaurant/operating-hours`,
    TABLES: `${PMS_API}/restaurant/tables`,
    REPORTS_REVENUE: `${PMS_API}/restaurant/reports/revenue`,
};

// ─── Finance Endpoints ────────────────────────────────────────────────────────
export const FINANCE = {
    DASHBOARD: `${PMS_API}/finance/dashboard`,
    TRANSACTIONS: `${PMS_API}/finance/transactions`,
    SHIFTS_OPEN: `${PMS_API}/finance/shifts/open`,
    SHIFTS_ACTIVE: `${PMS_API}/finance/shifts/active`,
    SHIFTS_DECLARE: `${PMS_API}/finance/shifts/declare`,
    SHIFTS_CASH_DROP: `${PMS_API}/finance/shifts/cash-drop`,
    SHIFT_LIQUIDITY_SUMMARY: (id) => `${PMS_API}/finance/shifts/${id}/liquidity-summary`,
    FOLIO_BY_ID: (id) => `${PMS_API}/finance/folios/${id}`,
    FOLIO_CHARGES: (id) => `${PMS_API}/finance/folios/${id}/charges`,
    FOLIO_PAYMENTS: (id) => `${PMS_API}/finance/folios/${id}/payments`,
    FOLIO_REFUNDS: (id) => `${PMS_API}/finance/folios/${id}/refunds`,
    EXPENSES: `${PMS_API}/finance/expenses`,
    NIGHT_AUDIT: `${PMS_API}/finance/night-audit`,
    NIGHT_AUDIT_HISTORY: `${PMS_API}/finance/night-audit/history`,
    NIGHT_AUDIT_EXPECTED_ARRIVALS: `${PMS_API}/finance/night-audit/expected-arrivals`,
    NIGHT_AUDIT_EXPECTED_DEPARTURES: `${PMS_API}/finance/night-audit/expected-departures`,
    NIGHT_AUDIT_ROOM_DISCREPANCIES: `${PMS_API}/finance/night-audit/room-status-discrepancies`,
    NIGHT_AUDIT_UNSETTLED_FOLIOS: `${PMS_API}/finance/night-audit/unsettled-folios`,
    NIGHT_AUDIT_CITY_LEDGER: `${PMS_API}/finance/night-audit/city-ledger`,
    NIGHT_AUDIT_RECONCILE_PAYMENTS: `${PMS_API}/finance/night-audit/reconcile-payments`,
    NIGHT_AUDIT_GENERATE_REPORT: `${PMS_API}/finance/night-audit/generate-report`,
    NIGHT_AUDIT_EXCEPTION_REPORT: `${PMS_API}/finance/night-audit/exception-report`,
    NIGHT_AUDIT_UNPOSTED_CHARGES: `${PMS_API}/finance/night-audit/unposted-charges`,
    NIGHT_AUDIT_VOID_TRACKER: `${PMS_API}/finance/night-audit/void-tracker`,
    EXPORT: `${PMS_API}/finance/export`,
    EDIT_FOLIO_CHARGE: (id, chargeId) => `${PMS_API}/finance/folios/${id}/charges/${chargeId}`,
    VOID_FOLIO_CHARGE: (id, chargeId) => `${PMS_API}/finance/folios/${id}/charges/${chargeId}`,
    FINANCIAL_TRENDS: `${PMS_API}/finance/financial-trends`
};

// ─── Reports Endpoints ────────────────────────────────────────────────────────
export const REPORTS = {
    DASHBOARD: `${PMS_API}/reports/dashboard`,
    OCCUPANCY: `${PMS_API}/reports/occupancy`,
    ARRIVALS_DEPARTURES: `${PMS_API}/reports/arrivals-departures`,
    RESERVATIONS: `${PMS_API}/reports/reservations`,
    CANCELLATIONS: `${PMS_API}/reports/cancellations`,
    HOUSEKEEPING: `${PMS_API}/reports/housekeeping`,
    RESTAURANT_REVENUE: `${PMS_API}/reports/restaurant-revenue`,
    PNL: `${PMS_API}/reports/pnl`,
    INVENTORY: `${PMS_API}/reports/inventory`,
    STAFF_ATTENDANCE: `${PMS_API}/reports/staff-attendance`,
    RATE_VARIANCE: `${PMS_API}/reports/rate-variance`,
    SCHEDULED: `${PMS_API}/reports/scheduled`,
};

// ─── Executive Dashboard Endpoints ──────────────────────────────────────────
export const EXECUTIVE_DASHBOARD = `${PMS_API}/dashboard`;
export const WEEKLY_CHART = `${PMS_API}/dashboard/weekly-chart`;
export const REVENUE_BREAKDOWN = `${PMS_API}/dashboard/revenue-breakdown`;
export const EXPENSE_BREAKDOWN = `${PMS_API}/dashboard/expenses`;
export const ACTIVITY_LOG = `${PMS_API}/dashboard/activity-log`;
export const DASHBOARD_ALERTS = `${PMS_API}/dashboard/alerts`;
export const DISMISS_ALERT = (id) => `${PMS_API}/dashboard/alerts/${id}/dismiss`;
export const RESOLVE_ALERT = (id) => `${PMS_API}/dashboard/alerts/${id}/resolve`;
export const DASHBOARD_PREFERENCES = `${PMS_API}/dashboard/preferences`;

// ─── Direct Booking Engine Endpoints ──────────────────────────────────────────
export const BOOKING_ENGINE = {
    SEARCH:          `${PMS_API}/public/booking-engine/search`,
    HOTELS:          `${PMS_API}/public/booking-engine/hotels`,
    HOTEL:           `${PMS_API}/public/booking-engine/hotel`,
    VALIDATE_PROMO:  `${PMS_API}/public/booking-engine/validate-promo`,
    RESERVATIONS:    `${PMS_API}/public/booking-engine/reservations`,
    VOUCHER:         (bookingReference) => `${PMS_API}/public/booking-engine/reservations/${bookingReference}`,
    ENQUIRY:         `${PMS_API}/public/booking-engine/enquiry`,
    INITIATE_RAZORPAY: `${PMS_API}/public/booking-engine/razorpay/initiate`,
    VERIFY_RAZORPAY:   `${PMS_API}/public/booking-engine/razorpay/verify`,
};

// ─── Public Guest Endpoints ───────────────────────────────────────────────────
export const PUBLIC_GUEST = {
    UPLOAD:          `${PMS_API}/public/guest/upload`,
    CHECKIN:         (token) => `${PMS_API}/public/guest/checkin/${token}`,
    CHECKIN_SUBMIT:  (token) => `${PMS_API}/public/guest/checkin/${token}/submit`,
};



